import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// FDI two-digit numbering: quadrants 1-4, teeth 1-8 within each quadrant.
// Layout: upper jaw = quadrants 1 (right) | 2 (left). Lower jaw = quadrant 4 (right) | 3 (left).
const UPPER_RIGHT = ["18","17","16","15","14","13","12","11"];
const UPPER_LEFT  = ["21","22","23","24","25","26","27","28"];
const LOWER_LEFT  = ["38","37","36","35","34","33","32","31"];
const LOWER_RIGHT = ["48","47","46","45","44","43","42","41"];

const STATES = [
  { id: "healthy",   label: "Healthy",   color: "#FFFFFF", border: "#D4D4D8", text: "#0A0A0A" },
  { id: "cavity",    label: "Cavity",    color: "#FECACA", border: "#EF4444", text: "#7F1D1D" },
  { id: "filled",    label: "Filled",    color: "#BFDBFE", border: "#3B82F6", text: "#1E3A8A" },
  { id: "crown",     label: "Crown",     color: "#FEF08A", border: "#EAB308", text: "#713F12" },
  { id: "rct",       label: "Root canal",color: "#DDD6FE", border: "#8B5CF6", text: "#4C1D95" },
  { id: "missing",   label: "Missing",   color: "#E5E5E5", border: "#A1A1AA", text: "#3F3F46" },
  { id: "implant",   label: "Implant",   color: "#A7F3D0", border: "#10B981", text: "#064E3B" },
];

function getState(id) { return STATES.find(s => s.id === id) || STATES[0]; }

export default function ToothChart({ patientId, readOnly }) {
  const [teeth, setTeeth] = useState({});
  const [note, setNote] = useState("");
  const [activeState, setActiveState] = useState("cavity");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const r = await api.get(readOnly ? "/portal/tooth-chart" : `/patients/${patientId}/tooth-chart`);
    setTeeth(r.data.teeth || {});
    setNote(r.data.note || "");
    setDirty(false);
  };
  useEffect(() => { load(); }, [patientId, readOnly]);

  const clickTooth = (id) => {
    if (readOnly) return;
    setTeeth((t) => {
      const cur = t[id];
      const next = { ...t };
      if (cur === activeState) {
        delete next[id];
      } else {
        next[id] = activeState;
      }
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/patients/${patientId}/tooth-chart`, { teeth, note });
      toast.success("Dental chart saved");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="tooth-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] font-black text-zinc-700">Dental chart (FDI)</div>
        {!readOnly && dirty && (
          <button onClick={save} disabled={busy} className="text-xs uppercase tracking-[0.2em] font-bold text-white bg-[#E60000] hover:bg-[#CC0000] px-3 py-1.5 rounded-sm disabled:opacity-60" data-testid="tc-save">
            {busy ? "Saving…" : "Save chart"}
          </button>
        )}
      </div>

      {!readOnly && (
        <div className="flex flex-wrap gap-1 mb-3">
          {STATES.map((s) => (
            <button key={s.id} onClick={()=>setActiveState(s.id)}
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border rounded-sm transition-colors ${activeState===s.id?"ring-2 ring-offset-1 ring-zinc-900":""}`}
              style={{background: s.color, borderColor: s.border, color: s.text}}
              data-testid={`tc-state-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm p-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1 text-center">Upper jaw</div>
        <div className="grid gap-1" style={{gridTemplateColumns: "repeat(16, minmax(0, 1fr))"}}>
          {[...UPPER_RIGHT, ...UPPER_LEFT].map((id, i) => (
            <Tooth key={id} id={id} state={getState(teeth[id])} onClick={()=>clickTooth(id)} accent={i===7||i===8} readOnly={readOnly}/>
          ))}
        </div>

        <div className="my-2 border-t border-dashed border-zinc-300"/>

        <div className="grid gap-1" style={{gridTemplateColumns: "repeat(16, minmax(0, 1fr))"}}>
          {[...LOWER_RIGHT, ...LOWER_LEFT].map((id, i) => (
            <Tooth key={id} id={id} state={getState(teeth[id])} onClick={()=>clickTooth(id)} accent={i===7||i===8} bottom readOnly={readOnly}/>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1 text-center">Lower jaw</div>
      </div>

      {!readOnly && (
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">Notes</label>
          <textarea rows={2} value={note} onChange={(e)=>{setNote(e.target.value); setDirty(true);}}
            className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
            placeholder="Overall observations…" data-testid="tc-note"/>
        </div>
      )}

      {readOnly && Object.keys(teeth).length === 0 && (
        <div className="text-xs text-zinc-500 mt-2 text-center">No dental records yet.</div>
      )}
    </div>
  );
}

function Tooth({ id, state, onClick, accent, bottom, readOnly }) {
  return (
    <button onClick={onClick} disabled={readOnly} className={`flex flex-col items-center group ${readOnly?"cursor-default":""}`} data-testid={`tooth-${id}`}>
      {!bottom && <div className="text-[9px] text-zinc-500 mb-0.5">{id}</div>}
      <div
        className={`w-full aspect-square rounded-sm border text-[10px] font-bold flex items-center justify-center transition-transform ${readOnly?"":"group-hover:scale-110"}`}
        style={{background: state.color, borderColor: state.border, color: state.text, minHeight: 22}}
        title={state.label}>
        {accent && <span className="opacity-50">·</span>}
      </div>
      {bottom && <div className="text-[9px] text-zinc-500 mt-0.5">{id}</div>}
    </button>
  );
}
